import { Container } from '../ui/Container/Container';
import { useScrollReveal } from './useScrollReveal';

const MODEL_TAGS = [
  { name: 'Nano Banana Pro', hl: true },
  { name: 'Nano Banana 2', hl: false },
  { name: 'FLUX Kontext Pro', hl: true },
  { name: 'FLUX 2 Flex', hl: false },
  { name: 'FLUX Schnell', hl: false },
  { name: 'Seedream 4.5', hl: true },
  { name: 'Recraft V3', hl: false },
  { name: 'Ideogram V3', hl: false },
  { name: 'GPT Image 1.5', hl: false },
  { name: 'Luma Photon', hl: false },
  { name: 'Sora 2', hl: true },
  { name: 'Veo 3.1', hl: true },
  { name: 'Kling 3.0', hl: false },
  { name: 'Seedance 1.5', hl: false },
  { name: 'Hailuo-02', hl: false },
  { name: 'LTX 2.0', hl: false },
  { name: 'WAN 2.2', hl: false },
  { name: 'MiniMax', hl: false },
];

const CATEGORIES = [
  {
    abbr: 'Img',
    title: 'Image',
    count: '30+ models',
    bg: 'rgba(150,120,180,0.08)',
    color: '#9678b4',
    names: 'Nano Banana Pro, FLUX Kontext, Seedream 4.5, Recraft V3, Ideogram V3, GPT Image 1.5, Luma Photon, MiniMax, and more',
  },
  {
    abbr: 'Vid',
    title: 'Video',
    count: '20+ models',
    bg: 'rgba(91,138,181,0.08)',
    color: '#5b8ab5',
    names: 'Sora 2, Veo 3.1, Kling 3.0, Seedance 1.5, Hailuo-02, LTX 2.0, WAN 2.2 — text-to-video, image-to-video, with audio',
  },
  {
    abbr: 'Txt',
    title: 'Text',
    count: 'Free',
    bg: 'rgba(200,212,90,0.12)',
    color: '#a8b44a',
    names: 'Gemini 3 Flash, Gemini 2.0 Flash — long-form text generation with creativity control. Zero credits.',
  },
];

function TagChip({ name, hl }: { name: string; hl: boolean }) {
  return (
    <span
      className={`flex-shrink-0 px-3 py-[7px] rounded-full text-[0.7rem] whitespace-nowrap border ${
        hl
          ? 'border-primary-500 bg-[rgba(91,138,181,0.08)] text-[#1d2939]'
          : 'border-black/[0.06] bg-white text-[#5a6478]'
      }`}
    >
      {name}
    </span>
  );
}

export function LandingModels() {
  const ref = useScrollReveal<HTMLDivElement>();

  return (
    <section className="py-12 md:py-20 bg-[#f5f7fa]" id="models">
      <Container size="lg" className="max-w-[1100px]">
        <div ref={ref} className="lp-reveal text-center">
          <p className="font-mono text-xs uppercase tracking-[0.14em] text-primary-500 mb-3">
            60+ models
          </p>
          <h2
            className="text-[clamp(1.75rem,4vw,2.6rem)] font-semibold leading-[1.15] tracking-tight mb-3"
            style={{ color: '#1d2939' }}
          >
            Every model you need.
            <br />
            Zero extra subscriptions.
          </h2>
          <p className="text-[clamp(0.95rem,2vw,1.1rem)] text-[#5a6478] max-w-[540px] mx-auto leading-relaxed">
            Image, video, and text models from Google, OpenAI, Black Forest Labs, ByteDance, Kuaishou, and more.
          </p>
        </div>

        {/* Marquee model tags */}
        <div className="relative overflow-hidden mt-6 py-3" style={{ maskImage: 'linear-gradient(90deg, transparent, black 5%, black 95%, transparent)', WebkitMaskImage: 'linear-gradient(90deg, transparent, black 5%, black 95%, transparent)' }}>
          <div className="flex gap-1.5 lp-marquee">
            {/* Render tags twice for seamless loop */}
            {MODEL_TAGS.map((m, i) => (
              <TagChip key={`a-${i}`} name={m.name} hl={m.hl} />
            ))}
            {MODEL_TAGS.map((m, i) => (
              <TagChip key={`b-${i}`} name={m.name} hl={m.hl} />
            ))}
          </div>
        </div>

        {/* Category cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
          {CATEGORIES.map((c) => (
            <div
              key={c.title}
              className="bg-white border border-black/[0.06] rounded-[14px] p-[1.1rem]"
            >
              <div className="flex items-center gap-2 mb-2.5">
                <div
                  className="w-[30px] h-[30px] rounded-lg flex items-center justify-center text-[0.8rem] font-semibold"
                  style={{ background: c.bg, color: c.color }}
                >
                  {c.abbr}
                </div>
                <div>
                  <div className="text-[0.85rem] font-semibold text-[#1d2939]">{c.title}</div>
                  <div className="font-mono text-[0.6rem] text-[#8c95a6]">{c.count}</div>
                </div>
              </div>
              <div className="text-[0.72rem] text-[#5a6478] leading-[1.7]">{c.names}</div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}
